/*
 * PAWFEEDER product page runtime.
 *
 * CHG-2026-016 moved pricing to native Shopify variants: the selected variant
 * *is* the price, so this file never computes one. It only keeps the page in
 * sync with the buyer's selection - variant, quantity in pieces, gallery,
 * item number, Data Overview - and submits the product form. Because the price
 * is read from the variant everywhere, the product page, the cart and the
 * checkout cannot disagree.
 *
 * Pricing, tier ladders, carton conversion and the wholesale-checkout gate were
 * removed with the old metafield model.
 *
 * CHG-2026-023 brought back a minimum order quantity, but as a *floor*, not as
 * a price ladder: the tier the buyer picks is still a plain variant with its own
 * unit price, and the "Quantity Range" option value on that variant says the
 * quantity the tier is priced from. So this file only makes sure the submitted
 * quantity can never sit below the selected tier, and it rewrites the hidden
 * quantity immediately before add-to-cart so a hand-edited value is discarded.
 *
 * The Sample tier is the exception: its option value states no number, so the
 * payload marks it with `moq: 0` and the floor drops to 1 - the buyer may take
 * any quantity at that tier's unit price. `moq: null` is a different case and
 * means the product declares no quantity option at all, which is when the
 * server-rendered floor setting applies.
 */
(function () {
  'use strict';

  var I18N = window.PF_I18N || {};

  var root = document.querySelector('[data-pf-product]');
  if (!root) return;

  var form = root.querySelector('form[id^="PawfeederProductForm"]') || document.querySelector('form[id^="PawfeederProductForm"]');
  var pieceInput = root.querySelector('[data-pf-piece-input]');
  var piecesInput = root.querySelector('[data-pf-pieces]');
  var addButton = root.querySelector('[data-pf-add-to-cart]');
  var status = root.querySelector('[data-pf-purchase-status]');
  var errorBox = root.querySelector('[data-pf-purchase-error]');
  var variantSelect = root.querySelector('[data-pf-variant-select]');
  var variantData = root.querySelector('[data-pf-variants]');
  var groups = Array.prototype.slice.call(root.querySelectorAll('[data-pf-option-group]'));
  var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  var variants = [];
  if (variantData) {
    try { variants = JSON.parse(variantData.textContent); } catch (error) { variants = []; }
  }
  var currentVariant = null;

  function money(cents) {
    if (window.PawfeederUI && window.PawfeederUI.money) return window.PawfeederUI.money(cents, currency);
    return (Number(cents || 0) / 100).toFixed(2);
  }

  function compactMoney(cents, inCurrency) {
    if (window.PawfeederUI && window.PawfeederUI.compactMoney) return window.PawfeederUI.compactMoney(cents, inCurrency || currency);
    return money(cents);
  }

  /* The Add to cart label prints the amount in symbol form, "$12,000.00" rather
     than "USD 12,000.00" (CHG-2026-027). money() stays the default everywhere
     else, so prices, cart lines and the drawer still follow the store's
     `show_currency_code` setting and keep printing the ISO code. */
  function symbolMoney(cents) {
    if (window.PawfeederUI && window.PawfeederUI.moneySymbol) return window.PawfeederUI.moneySymbol(cents);
    return money(cents);
  }

  function addLabel() {
    return (addButton && addButton.getAttribute('data-pf-label')) || (I18N.add_to_cart || 'Add to cart');
  }

  /* ------------------------------------------------------------- quantity */

  /* The minimum comes from the selected variant's own "Quantity Range" value, so
     no round trip is needed when the buyer switches tier. The payload carries
     three states: `moq: null` means the product declares no quantity option, so
     fall back to the server-rendered `min` attribute (the block's floor
     setting); `moq: 0` means the Sample tier, which has no minimum at all, so
     the floor is 1 and the buyer's own number stands; a positive `moq` is the
     tier's minimum. Only `null` may fall through to the DOM, otherwise a tier
     with no floor would inherit whatever floor the previous tier painted. */
  function minPieces() {
    var declared = currentVariant ? currentVariant.moq : null;
    if (declared !== null && declared !== undefined && declared !== '') {
      var tier = Math.floor(Number(declared));
      if (Number.isFinite(tier)) return tier > 1 ? tier : 1;
    }
    var value = Number(piecesInput && piecesInput.min) || 1;
    return value > 0 ? Math.floor(value) : 1;
  }

  function requestedPieces() {
    if (!piecesInput) return minPieces();
    var typed = Math.floor(Number(piecesInput.value));
    if (!Number.isFinite(typed)) typed = minPieces();
    return Math.max(minPieces(), typed);
  }

  /* CHG-2026-027 removed the tier minimum note from the page, so nothing here
     rewrites a sentence any more. The floor is still enforced - by `min` on the
     input, by requestedPieces() and by the minus button going disabled at the
     floor - and the tier button the buyer pressed already reads "200pcs", which
     is what made the note a third restatement of the same fact. */
  function paintQuantity() {
    var pieces = requestedPieces();
    var minimum = minPieces();
    if (piecesInput) piecesInput.value = pieces;
    if (pieceInput) pieceInput.value = pieces;
    if (piecesInput) piecesInput.min = String(minimum);
    if (minus) minus.disabled = pieces <= minimum;

    var label = root.querySelector('[data-pf-pieces-label]');
    if (label) label.textContent = pieces + (pieces === 1 ? (I18N.piece_one || ' pc') : (I18N.piece_many || ' pcs'));

    var unitPrice = root.querySelector('[data-pf-current-unit-price]');
    if (unitPrice) {
      unitPrice.textContent = currentVariant ? money(currentVariant.price) + (I18N.per_pc || ' / pc') : '—';
    }
    var subtotal = root.querySelector('[data-pf-estimated-subtotal]');
    if (subtotal) {
      subtotal.textContent = currentVariant ? money(Number(currentVariant.price) * pieces) : '—';
    }
    syncQuoteLines(pieces);
    paintAddLabel(pieces);
    return pieces;
  }

  /* The goods subtotal rides on the Add to cart label (CHG-2026-026) so the
     buyer sees the number they are about to commit to. It lives here rather
     than in paintPurchaseState() because the quantity is what moves it: typing
     in the stepper repaints the quantity and nothing else. The sold-out wording
     stays with paintPurchaseState(), which owns availability. */
  function paintAddLabel(pieces) {
    if (!addButton || !currentVariant || !currentVariant.available) return;
    var label = addLabel() + ' - ' + symbolMoney(Number(currentVariant.price) * pieces);
    addButton.textContent = label;
    addButton.dataset.originalLabel = label;
  }

  function stepPieces(delta) {
    if (!piecesInput) return;
    piecesInput.value = Math.max(minPieces(), requestedPieces() + delta);
    paintQuantity();
  }

  /* Editing the quantity or the option retires the previous attempt's verdict
     (CHG-2026-033). Shopify's sentence describes the add that was just refused,
     so it must not stay on screen reading as a comment about the new selection -
     only the next submit may replace it. */
  var minus = root.querySelector('[data-pf-qty-minus]');
  if (minus) minus.addEventListener('click', function () { hideStatus(); stepPieces(-1); });
  var plus = root.querySelector('[data-pf-qty-plus]');
  if (plus) plus.addEventListener('click', function () { hideStatus(); stepPieces(1); });
  if (piecesInput) {
    piecesInput.addEventListener('input', function () { hideStatus(); paintQuantity(); });
    piecesInput.addEventListener('blur', function () { hideStatus(); paintQuantity(); });
  }

  /* --------------------------------------------------------- quote context */

  /* Enquiry dialogs carry the current selection. No carton or tier survives the
     pricing change, so only the piece quantity, the variant and the unit price
     are rewritten here. */
  function syncQuoteLines(pieces) {
    var unitPrice = currentVariant ? money(currentVariant.price) : '';
    root.querySelectorAll('[data-pf-quote-line]').forEach(function (line) {
      var scope = line.parentNode || root;
      var piecesNode = scope.querySelector('[data-pf-quote-pieces]');
      var subtotalNode = scope.querySelector('[data-pf-quote-subtotal]');
      if (piecesNode) piecesNode.textContent = pieces + (I18N.piece_many || ' pcs');
      if (subtotalNode && currentVariant) subtotalNode.textContent = money(Number(currentVariant.price) * pieces);
      if (!line.dataset.pfQuoteValue) return;
      line.value = line.dataset.pfQuoteValue
        .replace(/pcs: \d+/, 'pcs: ' + pieces)
        .replace(/unit: [^|]*/, 'unit: ' + unitPrice + ' ')
        .replace(/subtotal: [^|]+/, 'subtotal: ' + (currentVariant ? money(Number(currentVariant.price) * pieces) : '') + ' ')
        .replace(/\s{2,}/g, ' ')
        .replace(/\|\s*\|/g, '|')
        .trim();
    });
  }

  /* ------------------------------------------------------------- painting */

  function paintAvailability() {
    if (!variants.length) return;
    groups.forEach(function (group, groupIndex) {
      group.querySelectorAll('[data-pf-option-value]').forEach(function (wrapper) {
        var radio = wrapper.querySelector('[data-pf-option-input]');
        if (!radio) return;
        var available = isValueAvailable(groupIndex, radio.value) || radio.checked;
        radio.disabled = !available;
        wrapper.classList.toggle('is-unavailable', !available);
      });
    });
  }

  function paintOptionLegends() {
    groups.forEach(function (group) {
      var output = group.querySelector('[data-pf-option-selected]');
      if (!output) return;
      var checked = group.querySelector('[data-pf-option-input]:checked');
      output.textContent = checked ? checked.value : '';
    });
  }

  /*
   * CHG-2026-120. The shipping weight and volume table is written once by Liquid,
   * one <table> per size/capacity/style ladder, and this is the only thing the
   * client does to it: show the ladder the selected variant belongs to and shade
   * its row. The numbers are never recomputed here - a second formatter would be
   * a second rounding rule, and the two would drift apart the first time a value
   * lands on a boundary.
   *
   * The ladder is found by looking for the selected variant's own row rather than
   * by rebuilding a group key in JS: the key is derived from which options count
   * as "appearance only", and that list is a merchant setting the server already
   * applied. Recomputing it here would mean two sources of truth for one rule.
   */
  function paintLogistics(variant) {
    var tables = root.querySelectorAll('[data-pf-logistics] .pf-logistics');
    if (!tables.length) return;
    var variantId = variant ? String(variant.id) : '';
    tables.forEach(function (table) {
      var selectedRow = variantId ? table.querySelector('[data-pf-logistics-row="' + variantId + '"]') : null;
      table.hidden = !selectedRow;
      table.querySelectorAll('[data-pf-logistics-row]').forEach(function (row) {
        row.classList.toggle('is-selected', row === selectedRow);
      });
    });
  }

  function paintDataOverview(variant) {
    var overview = root.querySelector('[data-pf-data-overview]');
    if (!overview) return;
    var metricData = (variant.pawfeeder && variant.pawfeeder.dataOverview) || {};
    var validType = ['observed', 'estimated', 'demo'].indexOf(String(metricData.type || '').toLowerCase()) >= 0;
    var hasValue = function (value) { return value !== null && value !== undefined && value !== ''; };
    var hasMetric = ['totalGmvCents', 'hotItemRate', 'sales7d', 'positiveRating'].some(function (key) { return hasValue(metricData[key]); });
    overview.hidden = !validType || !hasMetric;

    var updatedDate = metricData.updatedAt ? new Date(metricData.updatedAt) : null;
    var validDate = updatedDate && !Number.isNaN(updatedDate.getTime());
    var ageDays = validDate ? (Date.now() - updatedDate.getTime()) / 86400000 : 0;
    var stale = validDate && ageDays > 14;
    overview.classList.toggle('is-stale', Boolean(stale));
    var statusPill = overview.querySelector('.pf-data-overview__status');
    if (statusPill) statusPill.classList.toggle('is-stale', Boolean(stale));

    var label = overview.querySelector('[data-pf-data-label]');
    if (label && validType) {
      var typeLabel = metricData.type === 'estimated' ? (I18N.data_estimated || 'Estimated · Amazon market data') : metricData.type === 'demo' ? (I18N.data_demo || 'Demo data (not for production)') : (I18N.data_observed || 'Observed data');
      var parts = [typeLabel];
      if (validDate) parts.push(ageDays < 7 ? (I18N.updated_this_week || 'Updated this week') : (I18N.updated_weeks_ago || 'Updated @@ weeks ago').replace('@@', Math.floor(ageDays / 7)));
      if (stale) parts.push((I18N.refresh_overdue || 'Refresh overdue'));
      /*
       * The collector's `source` string (ASIN + snapshot date) is deliberately
       * not part of the pill - it is an audit field. It still travels in the
       * payload, so re-showing it is a one-liner if that is ever wanted.
       */
      label.textContent = parts.join(' · ');
    }

    overview.querySelectorAll('[data-pf-metric]').forEach(function (metric) {
      var key = metric.dataset.pfMetric;
      var value = metricData[key];
      metric.hidden = !hasValue(value);
      var output = metric.querySelector('[data-pf-metric-value]');
      if (!output || !hasValue(value)) return;
      if (key === 'totalGmvCents') output.textContent = compactMoney(value, metricData.gmvCurrency);
      else if (key === 'sales7d') output.textContent = Number(value).toLocaleString(document.documentElement.lang || 'en');
      else output.textContent = String(value) + '%';
    });
  }

  /*
   * CHG-2026-086. Shopify decides `variant.available` per market, so the same
   * URL answers differently depending on where the visitor is: in a market the
   * product is not published to, every one of its variants comes back false.
   * The page then fell back to `selected_or_first_available_variant` - which,
   * with nothing available, is simply the first variant - and told the buyer
   * that a single option combination was sold out. That sentence blames the
   * wrong thing and sends them hunting through colours and tiers that are all
   * equally unbuyable.
   *
   * The server attribute is the authority because only Liquid can read
   * `product.available`; the derived fallback keeps cached HTML that predates
   * the attribute from reverting to the misleading wording.
   */
  function productUnavailable() {
    if (root.dataset.productAvailable === 'false') return true;
    if (root.dataset.productAvailable === 'true') return false;
    return variants.length > 0 && !variants.some(function (variant) { return variant.available; });
  }

  function paintPurchaseState() {
    var unavailable = !currentVariant || !currentVariant.available;
    var blockedHere = productUnavailable();
    root.querySelectorAll('[data-pf-add-to-cart],[data-pf-buy-now],[data-pf-sticky-buy]').forEach(function (button) {
      button.disabled = unavailable;
    });
    if (addButton && unavailable) {
      /* Only the sold-out wording is painted here. The normal label is written
         by paintAddLabel(), which appends the live goods subtotal - and because
         paint() runs paintQuantity() after this function, that version wins.
         Naming the variant is right when one combination is out and the rest
         are buyable, and wrong when nothing is buyable where this visitor is. */
      if (blockedHere) addButton.textContent = I18N.sold_out || 'Sold out';
      else addButton.textContent = currentVariant ? currentVariant.title + ' ' + (I18N.is_sold_out || 'is sold out') : addLabel();
    }
    var stickyPrice = root.querySelector('[data-pf-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = currentVariant ? money(currentVariant.price) : '—';
    var basePrice = root.querySelector('[data-pf-base-price]');
    if (basePrice) {
      basePrice.textContent = currentVariant ? money(currentVariant.price) : '—';
      /* The no-payload fallback above reads this attribute back, so it has to
         follow the live variant rather than keep the server-rendered one. */
      if (currentVariant) basePrice.setAttribute('data-price-cents', String(currentVariant.price));
    }
    paintDiscountBadge();
    var optionStatus = root.querySelector('[data-pf-option-status]');
    if (optionStatus) {
      if (blockedHere) optionStatus.textContent = I18N.unavailable_in_region || 'This product is not available for purchase in your region.';
      else if (!currentVariant) optionStatus.textContent = (I18N.option_unavailable || 'This option combination is unavailable. Choose another option.');
      else optionStatus.textContent = currentVariant.available ? (I18N.selected || 'Selected:') + ' ' + currentVariant.title : currentVariant.title + ' ' + (I18N.is_sold_out || 'is sold out');
    }
  }

  /*
   * CHG-2026-093. The discount badge is the one figure on this row that no
   * server render can keep true: Liquid stamps `Save N%` from whichever variant
   * Shopify had selected when the HTML was built, and every quantity tier of a
   * colour carries its own unit price - so a single tap on a tier left the badge
   * describing a price that was no longer on screen. The live page shipped
   * `Save 33%` (the sample tier's $6.66 against the $9.99 MSRP) beside the
   * $3.08 tier, which is really 69% off.
   *
   * The MSRP is per variant too - a variant metafield with the product's as the
   * fallback, see the payload in pawfeeder-variant-picker.liquid - so the
   * percentage is rebuilt here from the same two numbers Liquid used. The badge
   * element is always present when the block asks for one, and `hidden` alone
   * toggles it, which the global `[hidden]` reset in critical.css guarantees.
   */
  function paintDiscountBadge() {
    var badge = root.querySelector('[data-pf-price-badge]');
    if (!badge) return;
    var msrpCents = currentVariant && currentVariant.pawfeeder ? currentVariant.pawfeeder.msrpCents : null;
    var percent = 0;
    if (msrpCents && currentVariant && msrpCents > currentVariant.price) {
      percent = Math.round((msrpCents - currentVariant.price) * 100 / msrpCents);
    }
    if (percent > 0) {
      badge.textContent = (badge.getAttribute('data-pf-save-template') || 'Save @@%').replace('@@', percent);
      badge.hidden = false;
    } else {
      badge.hidden = true;
    }
  }

  /* --------------------------------------------------------------- gallery */

  /*
   * One media is on stage at a time above 767px, and the class that marks it is
   * `is-selected`: the same class the variant switch has always used to point at
   * the selected variant's own image (CHG-2026-085). Reusing it keeps the two
   * routes from disagreeing - switching colour moves the stage, and choosing a
   * thumbnail moves it without touching the variant.
   *
   * The scroll target differs by tier. Phones move the stage item inside the
   * snap carousel; tablet and desktop move the active thumbnail inside the rail
   * beside it, which holds sixteen 84px buttons and cannot show them all beside
   * a 589px stage.
   *
   * `is-selected` is only ever set on the wide tier when the caller names no
   * media. On phones the default stays with CSS, so a viewport that starts
   * narrow and is widened later still has the first media on stage through
   * `.pf-gallery:not([data-pf-gallery-ready])` rather than an empty column.
   */
  function paintGallery(mediaId, scroll) {
    var gallery = root.querySelector('[data-pf-gallery]');
    if (!gallery) return;
    var items = gallery.querySelectorAll('[data-media-id]');
    if (!items.length) return;
    var strip = root.querySelector('[data-pf-gallery-thumbs]');
    var thumbs = strip ? strip.querySelectorAll('[data-pf-gallery-thumb]') : [];

    var wanted = mediaId ? String(mediaId) : '';
    var active = wanted ? gallery.querySelector('[data-media-id="' + wanted + '"]') : null;
    if (!active && matchMedia('(min-width: 768px)').matches) active = items[0];
    if (!active) return;

    /*
     * CHG-2026-090. Every quantity tier of one colour carries that colour's own
     * image, so picking a tier does not move the stage at all - yet the scroll
     * below ran anyway, and on a phone `scrollIntoView` walks up to the document
     * and pulled the page back to the gallery on every tier tap. The scroll is
     * there to reveal a picture that just changed; when the stage already shows
     * this media there is nothing to reveal, so the buyer keeps their place.
     * The last media on stage is remembered on the gallery rather than in a
     * variable, so it survives every call whether or not `scroll` was true.
     */
    var activeId = active.getAttribute('data-media-id');
    var previousId = gallery.getAttribute('data-pf-gallery-active');

    items.forEach(function (item) { item.classList.toggle('is-selected', item === active); });
    thumbs.forEach(function (thumb) {
      var on = thumb.getAttribute('data-media-id') === activeId;
      thumb.classList.toggle('is-selected', on);
      if (on) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });

    if (scroll && previousId !== activeId) {
      var reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      var behavior = reduce ? 'auto' : 'smooth';
      if (matchMedia('(max-width: 767px)').matches) {
        active.scrollIntoView({ behavior: behavior, block: 'nearest', inline: 'start' });
      } else if (strip) {
        /*
         * The rail is vertical and is its own scroll container, so it is moved
         * by its own arithmetic rather than by `scrollIntoView` (CHG-2026-087).
         * That call walks every scrollable ancestor, and on a window shorter
         * than the page it dragged the document along whenever a colour chip
         * moved the stage - the buyer clicked a swatch and the page jumped out
         * from under the cursor. Centring the chosen button keeps the rail's
         * own position stable as the thumbs either side of it are scrolled past.
         */
        var current = strip.querySelector('.pf-gallery__thumb.is-selected');
        if (current) {
          var top = current.offsetTop - (strip.clientHeight - current.offsetHeight) / 2;
          strip.scrollTo({ top: Math.max(0, top), behavior: behavior });
        }
      }
    }
    gallery.setAttribute('data-pf-gallery-active', activeId);
    gallery.setAttribute('data-pf-gallery-ready', '');
  }

  /*
   * CHG-2026-088. A phone has no rail, so the carousel is the whole gallery and
   * the counter is the only thing that says how many media there are or which
   * one is on screen. It reads the carousel's own scroll position rather than
   * listening to the variant switch, because on a phone the buyer moves the
   * slide with a swipe: the two only agree if the position is read back.
   *
   * The step is measured from the first item rather than from `clientWidth`, so
   * the arithmetic stays right if the slide ever stops being exactly the
   * container width - a scrollbar or a reintroduced gap would otherwise shift
   * every reading by a slide.
   *
   * One frame per scroll is enough: a swipe fires scroll without pause, and
   * counting every event would run the same arithmetic dozens of times for one
   * gesture. `paint()` at the end covers the browser restoring a scroll position
   * on load, when no scroll event is fired for the position it restores.
   */
  function wireGalleryCounter() {
    var gallery = root.querySelector('[data-pf-gallery]');
    var counter = root.querySelector('[data-pf-gallery-counter]');
    if (!gallery || !counter) return;
    var items = gallery.querySelectorAll('[data-media-id]');
    if (!items.length) return;
    var digits = counter.querySelector('[data-pf-gallery-current]');
    var spoken = counter.querySelector('[data-pf-gallery-counter-text]');
    var template = counter.getAttribute('data-pf-gallery-counter-template') || '';
    var frame = 0;

    function paint() {
      frame = 0;
      var step = items[0].getBoundingClientRect().width;
      if (!step) return;
      var index = Math.round(gallery.scrollLeft / step);
      if (index < 0) index = 0;
      if (index > items.length - 1) index = items.length - 1;
      if (digits) digits.textContent = String(index + 1);
      if (spoken && template) spoken.textContent = template.replace('###', String(index + 1));
    }

    gallery.addEventListener('scroll', function () {
      if (frame) return;
      frame = requestAnimationFrame(paint);
    }, { passive: true });

    paint();
  }

  function paint() {
    paintPurchaseState();
    paintQuantity();
    return true;
  }

  /* ------------------------------------------------------------- variants */

  function selectedOptions() {
    return groups.map(function (group) {
      var checked = group.querySelector('[data-pf-option-input]:checked');
      return checked ? checked.value : '';
    });
  }

  function findVariant(values) {
    for (var i = 0; i < variants.length; i += 1) {
      var variant = variants[i];
      var matches = variant.options.every(function (value, index) { return value === values[index]; });
      if (matches) return variant;
    }
    return null;
  }

  function isValueAvailable(groupIndex, value) {
    var current = selectedOptions();
    return variants.some(function (variant) {
      if (variant.options[groupIndex] !== value) return false;
      for (var i = 0; i < variant.options.length; i += 1) {
        if (i === groupIndex) continue;
        if (current[i] && variant.options[i] !== current[i]) return false;
      }
      return variant.available;
    });
  }

  function setVariant(variant, updateUrl) {
    /* Switching the quantity tier resets the stepper to that tier's own minimum
       (CHG-2026-026). Without this the floor only ever ratcheted up: going
       1000PCS -> 50PCS left 1000 in the input, because requestedPieces() is a
       Math.max. Colour and size switches keep whatever the buyer typed. */
    var previousTier = currentVariant ? currentVariant.moq : undefined;
    currentVariant = variant;
    if (!variant) {
      paintLogistics(null);
      paint();
      paintAvailability();
      return;
    }
    if (piecesInput && variant.moq !== previousTier) {
      piecesInput.value = String(minPieces());
    }
    if (variantSelect) variantSelect.value = String(variant.id);
    root.dataset.productSku = variant.sku || '';

    var sku = root.querySelector('[data-pf-sku]');
    if (sku) sku.textContent = variant.sku || (I18N.not_assigned || 'Not assigned');
    if (pieceInput) pieceInput.value = requestedPieces();
    var skuProperty = root.querySelector('[data-pf-sku-property]');
    if (skuProperty) skuProperty.value = variant.sku || '';
    root.querySelectorAll('[data-pf-quote-sku]').forEach(function (node) { node.textContent = variant.sku || (I18N.sku_not_assigned || 'SKU not assigned'); });
    root.querySelectorAll('[data-pf-quote-line]').forEach(function (line) {
      if (!line.dataset.pfQuoteValue) return;
      var options = variant.options || [];
      line.dataset.pfQuoteValue = line.dataset.pfQuoteValue
        .replace(/item: [^|]*/, 'item: ' + (variant.sku || (I18N.sku_not_assigned || 'SKU not assigned')) + ' ')
        .replace(/color: [^|]*/, 'color: ' + (options[0] || '') + ' ')
        .replace(/size: [^|]*/, 'size: ' + (options[1] || '') + ' ');
    });

    paintDataOverview(variant);
    paintLogistics(variant);
    paintOptionLegends();

    if (variant.featured_media && variant.featured_media.id) {
      /*
       * `updateUrl` is false only for the initial paint, and the gallery must not
       * scroll the page while it is still loading.
       *
       * CHG-2026-098. That initial paint must also leave the wide tier on the
       * product's FIRST media instead of moving the stage to the default
       * variant's own photograph. Measured on the live theme (V1.35), entering
       * the PDP cold at 1440px: 20 media, the first is 46064311599422 (index 0),
       * the default variant is Grey / sample (54591011193150) whose
       * featured_media is 46064878977342 (index 15), and index 15 is what the
       * buyer saw - a colour shot where the main photograph belongs.
       * `paintGallery(null, false)` above has already put index 0 on stage and
       * marked the first thumbnail, so skipping this one call leaves them
       * agreeing with each other. A colour or size pick still calls it with
       * updateUrl true and moves the stage exactly as before (measured in the
       * same run: picking Blue put 46064228401470, index 16, on stage).
       *
       * The phone tier keeps the call. Its carousel is positioned by scroll, not
       * by `is-selected` - measured at 390px, entry is scrollLeft 0 with index 0
       * covering the centre, so it already opens on the main photograph - and
       * this call is also what writes the `data-pf-gallery-active` record the
       * scroll gate below reads. Dropping it would change a tier the owner asked
       * to leave alone.
       */
      var firstPaintOnWideTier = !updateUrl && matchMedia('(min-width: 768px)').matches;
      if (!firstPaintOnWideTier) {
        paintGallery(variant.featured_media.id, Boolean(updateUrl));
      }
    }

    if (updateUrl && history.replaceState) {
      history.replaceState({}, '', root.dataset.productUrl + '?variant=' + variant.id);
    }
    paintAvailability();
    paint();
  }

  root.querySelectorAll('[data-pf-option-input]').forEach(function (radio) {
    radio.addEventListener('change', function () {
      hideStatus();
      var values = selectedOptions();
      var match = findVariant(values);
      if (!match) {
        var position = Number(radio.dataset.optionPosition) - 1;
        var fallback = variants.filter(function (variant) { return variant.available && variant.options[position] === radio.value; })[0];
        if (fallback) {
          groups.forEach(function (group, index) {
            var nextRadio = group.querySelector('[data-pf-option-input][value="' + CSS.escape(fallback.options[index]) + '"]');
            if (nextRadio) nextRadio.checked = true;
          });
          match = fallback;
        }
      }
      setVariant(match || null, true);
    });
  });

  /* ---------------------------------------------------------- gallery init */

  /*
   * Runs before the first setVariant: a product whose variants carry no image
   * still needs one media on stage and a wired strip. A variant image wins over
   * that default because setVariant paints the gallery after this block.
   */
  root.querySelectorAll('[data-pf-gallery-thumb]').forEach(function (thumb) {
    thumb.addEventListener('click', function () {
      paintGallery(thumb.getAttribute('data-media-id'), true);
    });
  });
  wireGalleryCounter();
  paintGallery(null, false);

  if (variantSelect && variants.length) {
    var initial = variants.filter(function (variant) { return String(variant.id) === variantSelect.value; })[0] || variants[0];
    setVariant(initial, false);
  } else {
    currentVariant = {
      id: variantSelect ? Number(variantSelect.value) : null,
      price: Number(root.querySelector('[data-pf-base-price]')?.dataset.priceCents) || 0,
      available: true,
      sku: root.dataset.productSku || '',
      options: []
    };
    paint();
  }

  /* -------------------------------------------------------- cart submission */

  function formDataToItem(formElement) {
    var data = new FormData(formElement);
    var item = { id: String(data.get('id') || ''), quantity: Number(data.get('quantity')) || 1, properties: {} };
    data.forEach(function (value, key) {
      var match = /^properties\[(.+)\]$/.exec(key);
      if (match) item.properties[match[1]] = String(value);
    });
    return item;
  }

  /* CHG-2026-033: the busy state is shared. pawfeeder-core.js marks every submit
     button `aria-disabled` and the form `aria-busy` when a form is submitted,
     and it cannot know when an intercepted submit finishes - so nothing takes
     those marks off again unless the page that owns the fetch does it. Left on,
     the button keeps `.pf-button[aria-disabled="true"]`'s opacity .45 and its
     not-allowed cursor for the rest of the page's life: it still works, but it
     reads as dead and the buyer reloads to get it back. Same undo as
     pawfeeder-wishlist.js already does on its own form. */
  function setBusy(button, busy, busyLabel) {
    if (!button) return;
    var original = button.dataset.originalLabel || button.textContent;
    button.dataset.originalLabel = original;
    button.disabled = busy;
    if (busy) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      if (busyLabel) button.textContent = busyLabel;
    } else {
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      if (button.form) button.form.removeAttribute('aria-busy');
      button.textContent = original;
    }
  }

  function showStatus(message) {
    if (!status) return;
    status.textContent = message;
    status.hidden = false;
  }

  function hideStatus() {
    if (status) status.hidden = true;
    if (errorBox) errorBox.hidden = true;
  }

  function fail(message, button) {
    if (errorBox) {
      errorBox.textContent = message;
      errorBox.hidden = false;
      errorBox.focus();
    }
    setBusy(button, false);
  }

  function checkoutUrl() {
    return root.dataset.checkoutUrl || ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'checkout';
  }

  /* The note for a partly-accepted add (CHG-2026-035). It states a fact about the
     cart - the count the drawer line right above it is showing - because that is
     the one number we can prove: Shopify never says how much of the request it
     took, it only says the add failed. The count the buyer asked for stands in
     the stepper, so "your cart now holds 300 of it" and "the rest could not be
     added" together explain the gap without calling the buyer's choice a
     mistake, which is why this is a status line and not the red error box. */
  function partialNote(line) {
    var pieces = Math.max(0, Math.floor(Number(line && line.quantity) || 0));
    return (I18N.partial_add || 'Your cart now holds @@ pcs of this option - the rest could not be added.').replace('@@', pieces);
  }

  function addToCart(button, thenCheckout) {
    if (!form || !window.PawfeederCart || !window.fetch) return;
    hideStatus();
    /* Re-clamp right before submitting so the payload is the tier minimum even
       if the hidden quantity was edited after the last paint. */
    var pieces = paintQuantity();
    var minimum = minPieces();
    if (!pieces || pieces < minimum) {
      fail((I18N.below_minimum || 'This option starts at @@ pcs. Update the quantity and try again.').replace('@@', minimum), button);
      return;
    }
    setBusy(button, true, thenCheckout ? (I18N.opening_checkout || 'Opening checkout…') : (I18N.adding || 'Adding…'));
    window.PawfeederCart.addItems([formDataToItem(form)], { openDrawer: !thenCheckout })
      .then(function () {
        setBusy(button, false);
        paint();
        if (thenCheckout) location.assign(checkoutUrl());
        else showStatus(I18N.added_to_cart || 'Added to cart.');
      })
      .catch(function (failure) {
        /* CHG-2026-035: the add was refused, but pawfeeder-cart-drawer.js has
           already read the cart back, so a request that still left this option
           in the cart is a partial success rather than a failure. The drawer is
           open on the real line by now and all that is left is to say what the
           cart holds - the buyer is not sent to checkout from here, because the
           count they would pay for is smaller than the one they asked for and
           that is their call to make, not ours. */
        if (failure.partial) {
          setBusy(button, false);
          paint();
          showStatus(partialNote(failure.line));
          return;
        }
        fail(failure.message + ' ' + (I18N.review_options || 'Please review your options and try again.'), button);
      });
  }

  if (form) {
    form.addEventListener('submit', function () {
      /*
       * Runs for every submit path, including the accelerated checkout button,
       * which has to keep its native behaviour. Re-clamping the hidden quantity
       * here means the wallet payload is the tier minimum too.
       */
      paintQuantity();
    });
    form.addEventListener('submit', function (event) {
      /*
       * Only the Add to cart button is intercepted. The accelerated checkout
       * button rendered by `payment_button` posts the same form straight to
       * checkout, so it has to keep its native behaviour.
       */
      var submitter = event.submitter || form.querySelector('[data-pf-add-to-cart]');
      if (!submitter || !submitter.hasAttribute('data-pf-add-to-cart')) return;
      if (!window.PawfeederCart || !window.fetch) return;
      event.preventDefault();
      addToCart(submitter, false);
    });
  }

  var buyNow = root.querySelector('[data-pf-buy-now]');
  if (buyNow) buyNow.addEventListener('click', function () { addToCart(buyNow, true); });
  var stickyBuy = root.querySelector('[data-pf-sticky-buy]');
  if (stickyBuy) stickyBuy.addEventListener('click', function () { addToCart(stickyBuy, true); });

  /* ------------------------------------------------------ item number copy */

  var copyButton = root.querySelector('[data-pf-copy-sku]');
  if (copyButton) {
    copyButton.addEventListener('click', function () {
      var skuNode = root.querySelector('[data-pf-sku]');
      var value = skuNode ? skuNode.textContent.trim() : '';
      var feedback = copyButton.querySelector('[data-pf-copy-status]');
      function report(message) {
        copyButton.classList.add('is-copied');
        if (feedback) feedback.textContent = message;
        if (window.PawfeederUI && window.PawfeederUI.showToast) window.PawfeederUI.showToast(message);
        setTimeout(function () { copyButton.classList.remove('is-copied'); }, 1600);
      }
      if (!value) return;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(function () {
          report(copyButton.getAttribute('data-pf-copy-done') || (I18N.sku_copied || 'Item number copied.'));
        }).catch(function () {
          report((I18N.copy_failed || 'Copy failed. The item number is @@.').replace('@@', value));
        });
        return;
      }
      report((I18N.sku_is || 'The item number is @@.').replace('@@', value));
    });
  }

  paint();
})();
